import type { Database } from '@opti-sea/db-types';
import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { createContext, useContext } from 'react';
import { z } from 'zod';

/**
 * The dashboard talks to Supabase directly with the publishable key and the user's sign-in; row
 * level security decides what each person sees (PLAN §1.3). Writes go through SQL functions (D39).
 */
export type Db = SupabaseClient<Database>;

const envSchema = z.object({
  VITE_SUPABASE_URL: z.url(),
  VITE_SUPABASE_PUBLISHABLE_KEY: z.string().min(1),
});

/** Null when the build has no Supabase settings (shown as a setup problem, not a crash). */
export function createDb(env: Record<string, unknown>): Db | null {
  const parsed = envSchema.safeParse(env);
  if (!parsed.success) {
    return null;
  }
  return createClient<Database>(
    parsed.data.VITE_SUPABASE_URL,
    parsed.data.VITE_SUPABASE_PUBLISHABLE_KEY,
    { auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: false } },
  );
}

export const DbContext = createContext<Db | null>(null);

export function useDb(): Db {
  const db = useContext(DbContext);
  if (db === null) {
    throw new Error('DbContext is missing');
  }
  return db;
}

interface Result<T> {
  data: T | null;
  error: { message: string; code?: string } | null;
}

/** Error from the server, with the Postgres error code when there is one. */
export class ServerError extends Error {
  constructor(
    message: string,
    readonly code: string | undefined,
  ) {
    super(message);
    this.name = 'ServerError';
  }
}

/** The data of a Supabase call, or the error thrown so TanStack Query shows it. */
export function unwrap<T>(result: Result<T>): T {
  if (result.error !== null) {
    throw new ServerError(result.error.message, result.error.code);
  }
  if (result.data === null) {
    throw new ServerError('No data', undefined);
  }
  return result.data;
}

/** Every row of a query, page by page (the Data API returns at most 1000 rows per request). */
export async function fetchAll<T>(
  page: (from: number, to: number) => PromiseLike<Result<T[]>>,
  pageSize = 1000,
): Promise<T[]> {
  const rows: T[] = [];
  for (let from = 0; ; from += pageSize) {
    const batch = unwrap(await page(from, from + pageSize - 1));
    rows.push(...batch);
    if (batch.length < pageSize) {
      return rows;
    }
  }
}
